import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppLayout } from '@/routes/AppLayout';
import { Home } from '@/routes/Dashboard';
import { NotFound } from '@/routes/NotFound';
import { SignIn } from '@/routes/SignIn';
import { MaintenanceGate } from '@/routes/Maintenance';
import { People } from '@/routes/People';
import { TechnicianProfileDetail } from '@/routes/TechnicianProfileDetail';
import { Dispatch } from '@/routes/Dispatch';
import { Jobs } from '@/routes/Jobs';
import { JobDetail } from '@/routes/JobDetail';
import { Customers } from '@/routes/Customers';
import { CustomerDetail } from '@/routes/CustomerDetail';
import { PropertyDetail } from '@/routes/PropertyDetail';
import { LeadSourceCatalog } from '@/routes/LeadSourceCatalog';
import { SkillCatalog } from '@/routes/SkillCatalog';
import { Pricebook } from '@/routes/Pricebook';
import { Estimates } from '@/routes/Estimates';
import { EstimateDetail } from '@/routes/EstimateDetail';
import { PublicEstimate } from '@/routes/PublicEstimate';
import { PublicInvoice } from '@/routes/PublicInvoice';
import { PublicAppointmentEmails } from '@/routes/PublicAppointmentEmails';
import { Invoices } from '@/routes/Invoices';
import { InvoiceDetail } from '@/routes/InvoiceDetail';
import { InvoiceDraftLines } from '@/routes/InvoiceDraftLines';
import { PricebookCategories } from '@/routes/PricebookCategories';
import { Fleet } from '@/routes/Fleet';
import { FleetVehicleDetail } from '@/routes/FleetVehicleDetail';
import { Settings } from '@/routes/Settings';
import { UsersSettings } from '@/routes/settings/UsersSettings';
import { AcceptInvite } from '@/routes/AcceptInvite';
import { RequireAuth } from '@/auth/RequireAuth';
import { RequirePermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { getApiAccessToken } from '@/auth/tokens';
import { setAccessTokenProvider } from '@/lib/api';

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Office staff keep tabs open all day; refetching on focus keeps
        // dispatch views current without polling.
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: 1,
      },
    },
  });
}

/** Point the API client at MSAL for bearer tokens (silently refreshed). */
function useApiAuth() {
  const { instance } = useMsal();
  useEffect(() => {
    setAccessTokenProvider(() => getApiAccessToken(instance));
  }, [instance]);
}

export function AppRoutes() {
  useApiAuth();
  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      {/* The customer's estimate link (M2-07). ⚠️ OUTSIDE RequireAuth and
          AppLayout: the customer has no account, and the shell's own reads
          (/me, /account) would 401 for them. The page reads through
          `publicClient`, so nothing it does can send them to /signin. */}
      <Route path="/e/:token" element={<PublicEstimate />} />
      {/* The customer's invoice link (M2-16), outside the shell for the same
          reasons as /e/, and read through `publicClient` too. */}
      <Route path="/i/:token" element={<PublicInvoice />} />
      {/* The recipient's appointment-email page (M2-21), the link at the foot
          of every appointment email: outside the shell for the same reasons
          as /e/ and /i/, and read through `publicClient` too. */}
      <Route path="/u/:token" element={<PublicAppointmentEmails />} />
      {/* The invitee's page (M2-20). OUTSIDE RequireAuth and AppLayout for
          the same reason as /e/: the person has no user row yet, so the
          shell's /me and /account would 403. The bare /invite is where the
          page continues once the token has left the address bar, and where
          AuthProvider returns the tab after sign-in (auth/pendingInvite.ts). */}
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/invite" element={<AcceptInvite />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {/* M2-17: the office lands on the owner dashboard, a Technician on
            their own job list. `Home` decides from /me's permissions. */}
        <Route index element={<Home />} />
        <Route
          path="dispatch"
          element={
            <RequirePermission permission={Permissions.readAllJobs}>
              <Dispatch />
            </RequirePermission>
          }
        />
        {/* jobs.work, not jobs.read.all (M1-22): the list is the technician's
            way in on a phone, and the API narrows WHICH jobs come back — the
            same shape jobs/:jobId already uses below. */}
        <Route
          path="jobs"
          element={
            <RequirePermission permission={Permissions.workAssignedJobs}>
              <Jobs />
            </RequirePermission>
          }
        />
        {/* jobs.work, not jobs.read.all: a Technician has to be able to open
            their own assigned job, and the API narrows which JOB they may
            open — this route only gates whether they may open ANY job at
            all (M1-13). */}
        <Route
          path="jobs/:jobId"
          element={
            <RequirePermission permission={Permissions.workAssignedJobs}>
              <JobDetail />
            </RequirePermission>
          }
        />
        <Route
          path="customers"
          element={
            <RequirePermission permission={Permissions.readCustomers}>
              <Customers />
            </RequirePermission>
          }
        />
        {/* customers.manage (M1-28), where the list above is customers.read:
            managing the vocabulary is a write. NOT account.manage — that is
            Owner-only in the M0-22 matrix and a Dispatcher fills this field on
            every customer they create.

            ⚠️ Declared BEFORE customers/:customerId and safe regardless:
            React Router ranks a static segment above a dynamic one, so
            /customers/lead-sources cannot be swallowed as a customer id.
            Order here is for a reader, not for the matcher — but the pairing
            is exactly what M1-27 called "one careless route constraint away
            from colliding", so there is a test that holds it. */}
        <Route
          path="customers/lead-sources"
          element={
            <RequirePermission permission={Permissions.manageCustomers}>
              <LeadSourceCatalog />
            </RequirePermission>
          }
        />
        {/* The detail page is a link people paste to each other, so it is a
            route of its own rather than a panel on the list. */}
        <Route
          path="customers/:customerId"
          element={
            <RequirePermission permission={Permissions.readCustomers}>
              <CustomerDetail />
            </RequirePermission>
          }
        />
        {/* jobs.work, not readCustomers: a Technician has to be able to open
            a property they have a job at (M1-05) — the API narrows WHICH
            property, same shape as jobs/:jobId (M1-13). */}
        <Route
          path="properties/:propertyId"
          element={
            <RequirePermission permission={Permissions.workAssignedJobs}>
              <PropertyDetail />
            </RequirePermission>
          }
        />
        {/* estimates.build (M2-06): every role drafts estimates. The API
            narrows a technician to the ones on jobs they are on, and the
            screens hide the office's acts from anyone without
            customers.manage. */}
        <Route
          path="estimates"
          element={
            <RequirePermission permission={Permissions.buildEstimates}>
              <Estimates />
            </RequirePermission>
          }
        />
        <Route
          path="estimates/:estimateId"
          element={
            <RequirePermission permission={Permissions.buildEstimates}>
              <EstimateDetail />
            </RequirePermission>
          }
        />
        {/* customers.read, matching the API (M2-11/M2-12): the office's
            receivables. A Technician holds it not, and the API refuses them
            either way; Send and Void are customers.manage, which the page
            checks for itself. */}
        <Route
          path="invoices"
          element={
            <RequirePermission permission={Permissions.readCustomers}>
              <Invoices />
            </RequirePermission>
          }
        />
        <Route
          path="invoices/:invoiceId"
          element={
            <RequirePermission permission={Permissions.readCustomers}>
              <InvoiceDetail />
            </RequirePermission>
          }
        />
        {/* M2-10 (Kyle, 2026-10-02): a DRAFT's lines, for the technician who
            raised it — jobs.work here, and the API narrows it to a draft on a
            job they are on. Nothing else about invoices opens to them. */}
        <Route
          path="invoices/:invoiceId/lines"
          element={
            <RequirePermission permission={Permissions.workAssignedJobs}>
              <InvoiceDraftLines />
            </RequirePermission>
          }
        />
        {/* customers.read to look, matching the API (M2-02); the screen hides
            every write affordance from anyone without pricebook.manage. */}
        <Route
          path="pricebook"
          element={
            <RequirePermission permission={Permissions.readCustomers}>
              <Pricebook />
            </RequirePermission>
          }
        />
        {/* pricebook.manage: the vocabulary is only worth reaching for someone
            who can change it. Static segment, so it ranks above any dynamic
            sibling a later story adds (M1-28's note). */}
        <Route
          path="pricebook/categories"
          element={
            <RequirePermission permission={Permissions.managePricebook}>
              <PricebookCategories />
            </RequirePermission>
          }
        />
        <Route
          path="people"
          element={
            <RequirePermission permission={Permissions.readUsers}>
              <People />
            </RequirePermission>
          }
        />
        {/* jobs.work, not users.read (M1-24): every role holds it, and a
            Technician reaches only their OWN profile this way — the API
            narrows it, same shape as properties/:propertyId. */}
        <Route
          path="technicians/:userId"
          element={
            <RequirePermission permission={Permissions.workAssignedJobs}>
              <TechnicianProfileDetail />
            </RequirePermission>
          }
        />
        {/* users.manage, NOT the account.manage that gates /settings where
            M1-25's relationship-role catalog lives: account.manage is
            Owner-only in the M0-22 matrix, and this story's AC says
            "owner/admin manage the account's skill catalog". Reached from the
            roster rather than the nav, the same way technicians/:userId is. */}
        <Route
          path="people/skills"
          element={
            <RequirePermission permission={Permissions.manageUsers}>
              <SkillCatalog />
            </RequirePermission>
          }
        />
        {/* users.read, matching the API (M1-27). NOT account.manage: that is
            Owner-only in the M0-22 matrix, and a Dispatcher has to be able to
            look a truck up — the board's tooltip names it. A top-level section
            rather than something reached from People, unlike /people/skills:
            a fleet is a set of records with their own detail pages that people
            navigate to directly, not a vocabulary you go and maintain. */}
        <Route
          path="fleet"
          element={
            <RequirePermission permission={Permissions.readUsers}>
              <Fleet />
            </RequirePermission>
          }
        />
        <Route
          path="fleet/:vehicleId"
          element={
            <RequirePermission permission={Permissions.readUsers}>
              <FleetVehicleDetail />
            </RequirePermission>
          }
        />
        {/* company.manage (M2-19): Owner and Admin. The relationship-role
            catalog inside stays account.manage, checked by the page. */}
        <Route
          path="settings"
          element={
            <RequirePermission permission={Permissions.manageCompany}>
              <Settings />
            </RequirePermission>
          }
        />
        {/* users.manage (M2-20), the API's gate on every change: Owner and
            Admin. Under /settings (Kyle, 2026-10-08); the read-only roster at
            /people stays for users.read, which a Dispatcher holds. */}
        <Route
          path="settings/users"
          element={
            <RequirePermission permission={Permissions.manageUsers}>
              <UsersSettings />
            </RequirePermission>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export function App({ queryClient = createQueryClient() }: { queryClient?: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Wraps EVERY route, including /signin — an outage during sign-in
          must show maintenance, not a login loop against a dead API (M1-29). */}
      <MaintenanceGate>
        <AppRoutes />
      </MaintenanceGate>
    </QueryClientProvider>
  );
}
